import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from './api';
import {
  ArrowLeft, Edit2, Save, X, Plus, Trash2, FileText,
  Download, Upload, CheckCircle, Circle, ChevronDown, ChevronUp
} from 'lucide-react';

const TABS = ['Informação', 'Processos', 'Honorários', 'Documentos'];

const PROCESS_TIPOS = ['Direito Civil', 'Direito Penal', 'Direito de Família',
  'Direito do Trabalho', 'Direito Imobiliário', 'Direito Administrativo', 'Ato Notarial', 'Outro'];
const PROCESS_STATUS = ['Em Curso', 'Pendente', 'Encerrado', 'Arquivado'];

const STATUS_COLOR = {
  'Em Curso': 'bg-green-100 text-green-700',
  'Pendente': 'bg-yellow-100 text-yellow-700',
  'Encerrado': 'bg-gray-100 text-gray-600',
  'Arquivado': 'bg-blue-100 text-blue-700',
};

const formatBytes = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;
const formatDate = (s) => s ? new Date(s).toLocaleDateString('pt-PT') : '';

export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [client, setClient] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [processes, setProcesses] = useState([]);
  const [honorarios, setHonorarios] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [expandedProcess, setExpandedProcess] = useState(null);
  const fileRef = useRef();

  // Fetch all data
  useEffect(() => {
    loadClient();
    loadProcesses();
    loadHonorarios();
    loadDocuments();
  }, [id]);

  const loadClient = async () => {
    try { const r = await api.get(`/admin/clients/${id}`); setClient(r.data); setEditForm(r.data); }
    catch { navigate('/escritorio/dashboard'); }
  };
  const loadProcesses = async () => {
    try { const r = await api.get(`/admin/clients/${id}/processes`); setProcesses(r.data); } catch {}
  };
  const loadHonorarios = async () => {
    try { const r = await api.get(`/admin/clients/${id}/honorarios`); setHonorarios(r.data); } catch {}
  };
  const loadDocuments = async () => {
    try { const r = await api.get(`/admin/clients/${id}/documents`); setDocuments(r.data); } catch {}
  };

  // ── INFO TAB ──────────────────────────────────────────────────────
  const saveClient = async () => {
    try {
      await api.put(`/admin/clients/${id}`, editForm);
      setClient(editForm);
      setEditing(false);
    } catch {}
  };

  // ── PROCESSES TAB ─────────────────────────────────────────────────
  const [pForm, setPForm] = useState({ title: '', tipo: 'Direito Civil', status: 'Em Curso', description: '', notes: '' });
  const [showPForm, setShowPForm] = useState(false);

  const createProcess = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/clients/${id}/processes`, pForm); loadProcesses(); setShowPForm(false); setPForm({ title: '', tipo: 'Direito Civil', status: 'Em Curso', description: '', notes: '' }); }
    catch {}
  };
  const deleteProcess = async (pid) => {
    try { await api.delete(`/admin/processes/${pid}`); loadProcesses(); } catch {}
  };

  // ── HONORÁRIOS TAB ────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const [hForm, setHForm] = useState({ description: '', amount: '', date: today, paid: false });
  const [showHForm, setShowHForm] = useState(false);

  const createHonorario = async (e) => {
    e.preventDefault();
    try { await api.post(`/admin/clients/${id}/honorarios`, { ...hForm, amount: parseFloat(hForm.amount) }); loadHonorarios(); setShowHForm(false); setHForm({ description: '', amount: '', date: today, paid: false }); }
    catch {}
  };
  const togglePaid = async (h) => {
    try { await api.put(`/admin/honorarios/${h.id}`, { paid: !h.paid }); loadHonorarios(); } catch {}
  };
  const deleteHonorario = async (hid) => {
    try { await api.delete(`/admin/honorarios/${hid}`); loadHonorarios(); } catch {}
  };

  const totalHon = honorarios.reduce((s, h) => s + (h.amount || 0), 0);
  const paidHon = honorarios.filter(h => h.paid).reduce((s, h) => s + (h.amount || 0), 0);
  const pendingHon = totalHon - paidHon;

  // ── DOCUMENTS TAB ─────────────────────────────────────────────────
  const [uploading, setUploading] = useState(false);
  const [docNote, setDocNote] = useState('');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('notes', docNote);
      await api.post(`/admin/clients/${id}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadDocuments();
      setDocNote('');
    } catch {}
    finally { setUploading(false); fileRef.current.value = ''; }
  };
  const deleteDoc = async (did) => {
    try { await api.delete(`/admin/documents/${did}`); loadDocuments(); } catch {}
  };
  const downloadDoc = (did) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/documents/${did}/download?token=${localStorage.getItem('jcs_admin_token')}`, '_blank');
  };

  if (!client) return (
    <div className="min-h-screen bg-[#F4F2EE] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#6B2D3E] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F2EE]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center gap-4">
          <button onClick={() => navigate('/escritorio/dashboard')} className="text-[#AAAAAA] hover:text-[#6B2D3E] transition-colors" data-testid="back-btn">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <h1 className="font-serif text-lg text-[#1A1A1A] leading-tight">{client.name}</h1>
            {client.nif && <p className="font-sans text-xs text-[#AAAAAA]">NIF: {client.nif}</p>}
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-5xl mx-auto px-6 flex gap-0 border-t border-[#F4F2EE]">
          {TABS.map((t, i) => (
            <button key={i} onClick={() => setTab(i)}
              data-testid={`tab-${t.toLowerCase()}`}
              className={`font-sans text-xs uppercase tracking-widest px-5 py-3 transition-colors border-b-2 ${tab === i ? 'border-[#6B2D3E] text-[#6B2D3E]' : 'border-transparent text-[#AAAAAA] hover:text-[#4A4A4A]'}`}>
              {t} {i === 1 && processes.length > 0 && <span className="ml-1 text-[10px] bg-[#6B2D3E] text-white rounded-full px-1.5 py-0.5">{processes.length}</span>}
              {i === 2 && pendingHon > 0 && <span className="ml-1 text-[10px] bg-yellow-500 text-white rounded-full px-1.5 py-0.5">{honorarios.filter(h=>!h.paid).length}</span>}
              {i === 3 && documents.length > 0 && <span className="ml-1 text-[10px] bg-[#6B2D3E] text-white rounded-full px-1.5 py-0.5">{documents.length}</span>}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">

        {/* ── TAB 0: INFORMAÇÃO ── */}
        {tab === 0 && (
          <div className="bg-white border border-[#E5E0D8] p-6" data-testid="info-tab">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-serif text-lg text-[#1A1A1A]">Dados do Cliente</h2>
              {!editing ? (
                <button onClick={() => setEditing(true)} data-testid="edit-client-btn"
                  className="flex items-center gap-2 font-sans text-xs uppercase tracking-widest text-[#6B2D3E] hover:underline">
                  <Edit2 size={13} /> Editar
                </button>
              ) : (
                <div className="flex gap-3">
                  <button onClick={() => { setEditing(false); setEditForm(client); }}
                    className="flex items-center gap-1 font-sans text-xs text-[#AAAAAA] hover:text-[#1A1A1A]">
                    <X size={13} /> Cancelar
                  </button>
                  <button onClick={saveClient} data-testid="save-client-btn"
                    className="flex items-center gap-1 font-sans text-xs text-[#6B2D3E] hover:underline">
                    <Save size={13} /> Guardar
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {[
                { key: 'name', label: 'Nome Completo', type: 'text' },
                { key: 'nif', label: 'NIF', type: 'text' },
                { key: 'phone', label: 'Telefone', type: 'tel' },
                { key: 'email', label: 'Email', type: 'email' },
                { key: 'address', label: 'Morada', type: 'text', full: true },
              ].map(f => (
                <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                  <label className="font-sans text-xs uppercase tracking-widest text-[#AAAAAA] block mb-1">{f.label}</label>
                  {editing ? (
                    <input type={f.type} value={editForm[f.key] || ''} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] transition-colors" />
                  ) : (
                    <p className="font-sans text-sm text-[#1A1A1A]">{client[f.key] || '—'}</p>
                  )}
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="font-sans text-xs uppercase tracking-widest text-[#AAAAAA] block mb-1">Notas</label>
                {editing ? (
                  <textarea rows={3} value={editForm.notes || ''} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))}
                    className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] resize-none transition-colors" />
                ) : (
                  <p className="font-sans text-sm text-[#1A1A1A] whitespace-pre-wrap">{client.notes || '—'}</p>
                )}
              </div>
            </div>
            <p className="font-sans text-xs text-[#CCCCCC] mt-6">Cliente criado em {formatDate(client.created_at)}</p>
          </div>
        )}

        {/* ── TAB 1: PROCESSOS ── */}
        {tab === 1 && (
          <div data-testid="processes-tab">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-lg text-[#1A1A1A]">Processos <span className="font-sans text-sm text-[#AAAAAA]">({processes.length})</span></h2>
              <button onClick={() => setShowPForm(!showPForm)} data-testid="new-process-btn"
                className="flex items-center gap-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#5A2535] transition-colors">
                <Plus size={13} /> Novo Processo
              </button>
            </div>

            {/* Process Form */}
            {showPForm && (
              <form onSubmit={createProcess} className="bg-white border border-[#6B2D3E]/30 p-5 mb-5 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Título *</label>
                    <input required value={pForm.title} onChange={e => setPForm(p => ({ ...p, title: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E]" />
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Tipo</label>
                    <select value={pForm.tipo} onChange={e => setPForm(p => ({ ...p, tipo: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] bg-white">
                      {PROCESS_TIPOS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Estado</label>
                    <select value={pForm.status} onChange={e => setPForm(p => ({ ...p, status: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] bg-white">
                      {PROCESS_STATUS.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Descrição</label>
                    <textarea rows={2} value={pForm.description} onChange={e => setPForm(p => ({ ...p, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] resize-none" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Notas Internas</label>
                    <textarea rows={2} value={pForm.notes} onChange={e => setPForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] resize-none" />
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowPForm(false)}
                    className="px-4 py-2 border border-[#E5E0D8] font-sans text-xs uppercase tracking-widest hover:bg-[#F4F2EE] transition-colors">Cancelar</button>
                  <button type="submit" className="px-4 py-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest hover:bg-[#5A2535] transition-colors">Guardar</button>
                </div>
              </form>
            )}

            {processes.length === 0 ? (
              <div className="bg-white border border-[#E5E0D8] p-12 text-center">
                <p className="font-sans text-sm text-[#AAAAAA]">Ainda não há processos para este cliente.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {processes.map(p => (
                  <div key={p.id} className="bg-white border border-[#E5E0D8]" data-testid={`process-${p.id}`}>
                    <div className="flex items-center justify-between px-5 py-4 cursor-pointer" onClick={() => setExpandedProcess(expandedProcess === p.id ? null : p.id)}>
                      <div className="flex items-center gap-3">
                        <span className={`font-sans text-xs px-2 py-0.5 rounded-full ${STATUS_COLOR[p.status] || 'bg-gray-100 text-gray-600'}`}>{p.status}</span>
                        <div>
                          <p className="font-sans text-sm font-medium text-[#1A1A1A]">{p.title}</p>
                          <p className="font-sans text-xs text-[#AAAAAA]">{p.tipo} · {formatDate(p.created_at)}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {expandedProcess === p.id ? <ChevronUp size={15} className="text-[#AAAAAA]" /> : <ChevronDown size={15} className="text-[#AAAAAA]" />}
                        <button onClick={(e) => { e.stopPropagation(); deleteProcess(p.id); }}
                          className="p-1.5 text-[#CCCCCC] hover:text-red-500 transition-colors" data-testid={`delete-process-${p.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                    {expandedProcess === p.id && (p.description || p.notes) && (
                      <div className="px-5 pb-4 border-t border-[#F4F2EE] pt-3 space-y-2">
                        {p.description && <div><p className="font-sans text-xs text-[#AAAAAA] uppercase tracking-widest mb-1">Descrição</p><p className="font-sans text-sm text-[#1A1A1A] whitespace-pre-wrap">{p.description}</p></div>}
                        {p.notes && <div><p className="font-sans text-xs text-[#AAAAAA] uppercase tracking-widest mb-1">Notas Internas</p><p className="font-sans text-sm text-[#4A4A4A] whitespace-pre-wrap">{p.notes}</p></div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 2: HONORÁRIOS ── */}
        {tab === 2 && (
          <div data-testid="honorarios-tab">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: 'Total', value: totalHon, color: 'text-[#1A1A1A]' },
                { label: 'Recebido', value: paidHon, color: 'text-green-600' },
                { label: 'Pendente', value: pendingHon, color: 'text-yellow-600' },
              ].map(s => (
                <div key={s.label} className="bg-white border border-[#E5E0D8] p-4 text-center">
                  <p className="font-sans text-xs uppercase tracking-widest text-[#AAAAAA] mb-1">{s.label}</p>
                  <p className={`font-serif text-xl ${s.color}`}>{s.value.toFixed(2)} €</p>
                </div>
              ))}
            </div>

            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-lg text-[#1A1A1A]">Honorários <span className="font-sans text-sm text-[#AAAAAA]">({honorarios.length})</span></h2>
              <button onClick={() => setShowHForm(!showHForm)} data-testid="new-honorario-btn"
                className="flex items-center gap-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest px-4 py-2 hover:bg-[#5A2535] transition-colors">
                <Plus size={13} /> Novo Honorário
              </button>
            </div>

            {/* Honorario Form */}
            {showHForm && (
              <form onSubmit={createHonorario} className="bg-white border border-[#6B2D3E]/30 p-5 mb-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                  <div className="sm:col-span-2">
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Descrição *</label>
                    <input required value={hForm.description} onChange={e => setHForm(p => ({ ...p, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E]" />
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Valor (€) *</label>
                    <input required type="number" step="0.01" min="0" value={hForm.amount} onChange={e => setHForm(p => ({ ...p, amount: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E]" />
                  </div>
                  <div>
                    <label className="font-sans text-xs uppercase tracking-widest text-[#4A4A4A] block mb-1">Data</label>
                    <input type="date" value={hForm.date} onChange={e => setHForm(p => ({ ...p, date: e.target.value }))}
                      className="w-full px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E]" />
                  </div>
                  <div className="flex items-center gap-2 pt-5">
                    <input type="checkbox" id="paid-check" checked={hForm.paid} onChange={e => setHForm(p => ({ ...p, paid: e.target.checked }))} className="w-4 h-4 accent-[#6B2D3E]" />
                    <label htmlFor="paid-check" className="font-sans text-sm text-[#4A4A4A]">Pago</label>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setShowHForm(false)}
                    className="px-4 py-2 border border-[#E5E0D8] font-sans text-xs uppercase tracking-widest hover:bg-[#F4F2EE] transition-colors">Cancelar</button>
                  <button type="submit" className="px-4 py-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest hover:bg-[#5A2535] transition-colors">Guardar</button>
                </div>
              </form>
            )}

            {honorarios.length === 0 ? (
              <div className="bg-white border border-[#E5E0D8] p-12 text-center">
                <p className="font-sans text-sm text-[#AAAAAA]">Ainda não há honorários registados.</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E0D8] divide-y divide-[#F4F2EE]">
                {honorarios.map(h => (
                  <div key={h.id} className="flex items-center justify-between px-5 py-4" data-testid={`honorario-${h.id}`}>
                    <div className="flex items-center gap-3">
                      <button onClick={() => togglePaid(h)} className="flex-shrink-0" data-testid={`toggle-paid-${h.id}`}>
                        {h.paid
                          ? <CheckCircle size={18} className="text-green-500" />
                          : <Circle size={18} className="text-[#CCCCCC]" />}
                      </button>
                      <div>
                        <p className={`font-sans text-sm ${h.paid ? 'text-[#AAAAAA] line-through' : 'text-[#1A1A1A]'}`}>{h.description}</p>
                        <p className="font-sans text-xs text-[#AAAAAA]">{formatDate(h.date)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-serif text-base ${h.paid ? 'text-green-600' : 'text-[#1A1A1A]'}`}>{(h.amount || 0).toFixed(2)} €</span>
                      <button onClick={() => deleteHonorario(h.id)} className="text-[#CCCCCC] hover:text-red-500 transition-colors" data-testid={`delete-honorario-${h.id}`}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── TAB 3: DOCUMENTOS ── */}
        {tab === 3 && (
          <div data-testid="documents-tab">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-serif text-lg text-[#1A1A1A]">Documentos <span className="font-sans text-sm text-[#AAAAAA]">({documents.length})</span></h2>
            </div>

            {/* Upload area */}
            <div className="bg-white border border-dashed border-[#C5B8B0] p-6 mb-6 text-center">
              <Upload size={22} className="text-[#AAAAAA] mx-auto mb-3" />
              <p className="font-sans text-sm text-[#4A4A4A] mb-1">Carregar documento</p>
              <p className="font-sans text-xs text-[#AAAAAA] mb-4">PDF, Word, Excel, imagens · máx. 20 MB</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-md mx-auto">
                <input type="text" placeholder="Nota (opcional)" value={docNote} onChange={e => setDocNote(e.target.value)}
                  className="flex-1 px-3 py-2 border border-[#E5E0D8] font-sans text-sm focus:outline-none focus:border-[#6B2D3E] w-full sm:w-auto" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                  data-testid="upload-doc-btn"
                  className="flex items-center gap-2 bg-[#6B2D3E] text-white font-sans text-xs uppercase tracking-widest px-5 py-2.5 hover:bg-[#5A2535] transition-colors disabled:opacity-50 whitespace-nowrap">
                  {uploading ? 'A carregar...' : <><Upload size={13} /> Selecionar Ficheiro</>}
                </button>
              </div>
              <input ref={fileRef} type="file" className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.txt"
                onChange={handleUpload} data-testid="file-input" />
            </div>

            {documents.length === 0 ? (
              <div className="bg-white border border-[#E5E0D8] p-12 text-center">
                <p className="font-sans text-sm text-[#AAAAAA]">Ainda não há documentos arquivados.</p>
              </div>
            ) : (
              <div className="bg-white border border-[#E5E0D8] divide-y divide-[#F4F2EE]">
                {documents.map(d => (
                  <div key={d.id} className="flex items-center justify-between px-5 py-4" data-testid={`doc-${d.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText size={18} className="text-[#6B2D3E] flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-sans text-sm text-[#1A1A1A] truncate">{d.original_name}</p>
                        <p className="font-sans text-xs text-[#AAAAAA]">{formatBytes(d.size)} · {formatDate(d.created_at)}{d.notes ? ` · ${d.notes}` : ''}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <button onClick={() => downloadDoc(d.id)} data-testid={`download-doc-${d.id}`}
                        className="p-1.5 text-[#AAAAAA] hover:text-[#6B2D3E] transition-colors">
                        <Download size={15} />
                      </button>
                      <button onClick={() => deleteDoc(d.id)} data-testid={`delete-doc-${d.id}`}
                        className="p-1.5 text-[#AAAAAA] hover:text-red-500 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
